<template>
  <div class="h-full overflow-hidden">
    <BaseTable
      table-id="modifiedRangeItems"
      :headers="rangeItemHeaders"
      :list="modifiedTableData"
      :total-records="tableDataCount"
      :is-add-new-hidden="mode === 'view'"
      :hide-multiselect-delete="mode === 'view'"
      :selected-page="selectedPage"
      :items-per-page="10"
      hide-grouping
      isSavingLayoutHidden
      :edit-mode="mode === 'edit'"
      :is-add-all-button-active="false"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData($event)"
      @add-new="onAddMaterialAlterCodes">
      <template #actions="{ item }: any">
        <div
          class="flex items-center justify-center gap-1"
          v-if="item.isFromManager && mode === 'edit'">
          <ActionButton
            :is-enabled="!item.deleted"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as RangeItem)" />
        </div>
      </template>
      <template #custom="{ item, header, index }: any">
        <div v-if="header === 'quantity'">
          <BaseInput
            v-model="item[header]"
            type="number"
            color="base"
            flat
            class="h-8 bg-white"
            @changed="onInputChange(index)">
          </BaseInput>
        </div>
      </template>
    </BaseTable>
  </div>
  <RangeItemsModalAdd
    v-if="isModalAddActive"
    :list="materialAlterCodesList"
    :total-records="materialAlterCodesListCount"
    @multi-select="onMaterialAlterCodesSelect"
    @refresh-data="onAddMaterialAlterCodes"
    @close="isModalAddActive = false" />
  <BaseModalConfirm
    v-if="deleteRowItem && modalDeleteRow"
    @cancel="modalDeleteRow = false"
    @confirm="onDeleteSingle">
    <template #title>{{ $t('common.actions.delete') }}</template>
    <template #text>
      {{
        $t('messages.info.deleteSingle', {
          'item': deleteRowItem?.materialAlterCode?.alterCode?.name,
        })
      }}
    </template>
    <template #buttonText>{{ $t('common.actions.delete') }}</template>
  </BaseModalConfirm>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { getMaterialAlterCodesWithConditions } from '@/services/api/materials';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';
import BaseTable from '../common/BaseTable.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type { MaterialAlterCode } from '@/types/Materials';
import ActionButton from '../common/ActionButton.vue';
import { deepCopy } from '@/utils/data';
import { useToastStore } from '@/stores/toast';
import type { RangeItem } from '@/types/Ranges';
import RangeItemsModalAdd from '@/components/ranges/RangeItemsModalAdd.vue';
const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  mode: 'view' | 'edit';
  tableData: RangeItem[];
  allModifiedData: RangeItem[];
  tableDataCount: number;
  selectedPage: number;
  rangeId: string;
  addedDetails: RangeItem[];
}>();

const emit = defineEmits(['add', 'refreshTableData', 'delete', 'update']);

const rangeItemHeaders = computed(() => [
  {
    value: 'materialAlterCode.alterCode.name',
    text: $t('ranges.alterCodeName'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCode.alterCode.code',
    text: $t('ranges.alterCodeCode'),
    type: 'text',
    active: true,
  },
  {
    value: 'quantity',
    text: $t('ranges.quantity'),
    type: 'text',
    active: true,
    hasInlineEdit: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: props.mode == 'edit' },
]);

const modifiedTableData = ref(deepCopy<RangeItem[]>(props.tableData));
const isModalAddActive = ref(false);
const materialAlterCodesList = ref<MaterialAlterCode[]>([]);
const materialAlterCodesListCount = ref<number>();

async function loadData(data: TableFilterData) {
  const sortColumn =
    data && data.column !== undefined ? rangeItemHeaders.value[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
  };

  emit('refreshTableData', {
    ...params,
    rangeId: props.rangeId,
  });
}

async function onAddMaterialAlterCodes(data: TableFilterData | undefined) {
  materialAlterCodesList.value = [];
  const res = await getMaterialAlterCodesWithConditions({
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || '',
    sortAscending: data?.order === 'asc',
    rangeId: props.rangeId,
  });
  if (res) {
    const rangeMaterialAlterCodeIds = new Set(
      props.addedDetails
        .filter((x) => x.recordMode == RecordMode.Added)
        .map((item) => item.materialAlterCodeId)
    );
    materialAlterCodesList.value = res.data.filter(
      (item) => !rangeMaterialAlterCodeIds.has(item.id)
    );
    materialAlterCodesListCount.value = res.count;
  }
  isModalAddActive.value = true;
}

function onMaterialAlterCodesSelect(items: MaterialAlterCode[]) {
  isModalAddActive.value = false;
  const rangeItems: RangeItem[] = [];
  items.forEach((item) => {
    const newRangeItem: RangeItem = {
      id: uuidv4(),
      isFromManager: true,
      isActive: true,
      rangeId: props.rangeId,
      materialId: item.materialId || '',
      materialAlterCodeId: item.id,
      materialAlterCode: {
        id: item.id,
        alterCode: {
          id: item.alterCode.id,
          code: item.alterCode.code,
          name: item.alterCode.name,
        },
      },
      quantity: 0,
      recordMode: RecordMode.Added,
    };
    rangeItems.push(newRangeItem);
  });
  if (rangeItems.length) emit('add', rangeItems);
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<RangeItem>();
function onDeleteRow(item: RangeItem) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

const toastStore = useToastStore();

function onDeleteMultiselect(items: RangeItem[]) {
  const itemsToDelete = items.filter((item) => item.isFromManager);
  if (itemsToDelete.length === 0) {
    toastStore.info({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }

  emit('delete', itemsToDelete);
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;
  modalDeleteRow.value = false;
  emit('delete', [deleteRowItem.value]);
}

watch(
  () => props.tableData,
  (newData) => {
    modifiedTableData.value = deepCopy<RangeItem[]>(newData);
  },
  { deep: true }
);

function onInputChange(index: number) {
  if (modifiedTableData.value[index].recordMode !== RecordMode.Added)
    modifiedTableData.value[index].recordMode = RecordMode.Modified;
  emit('update', modifiedTableData.value[index]);
}
</script>
